import axios from "axios";
import config from "../config";

// Tarayıcı tarafı hata/bildirim kayıtlarını toplu halde sunucuya (/clientLog)
// gönderir. Sunucu storage/logs/notifications altına yazar, 7 günden eskileri
// siler. Buradaki sınırlar sunucuyu ve kullanıcının ağını şişirmemek için:
//  - Kayıtlar tamponda birikir, 10-15 sn'de bir tek istekle gider. Rastgele
//    kayma: soket sunucusu düştüğünde tüm kullanıcılar aynı saniyede yazmasın.
//  - Aynı olay 5 dk içinde tekrar ederse yeni kayıt açılmaz, sayaç artar.
//  - Bir sayfa açılışında en fazla MAX_PER_PAGE kayıt gönderilir.
//  - Sunucu 429 dönerse 5 dk susulur.
const ENDPOINT = "/clientLog";
const FLUSH_DELAY_MS = 10000;
const ERROR_FLUSH_DELAY_MS = 1000;
const FLUSH_JITTER_MS = 5000;
const REQUEST_TIMEOUT_MS = 10000;
const BATCH_SIZE = 50;
// Sunucu 64 KB üstünü reddeder; keepalive fetch'in de 64 KB sınırı var.
const MAX_BATCH_BYTES = 48000;
const MAX_QUEUE = 100;
const MAX_PER_PAGE = 300;
const DEDUP_WINDOW_MS = 5 * 60000;
const RATE_LIMIT_PAUSE_MS = 5 * 60000;
const TRAIL_SIZE = 20;
const MAX_STACK = 1000;

const session =
  Date.now().toString(36) +
  "-" +
  Math.random()
    .toString(36)
    .slice(2, 8);

let queue = [];
let trail = [];
const recent = new Map();
let sentTotal = 0;
let pausedUntil = 0;
let sending = false;
let flushTimer = null;
let flushDueAt = 0;
let installed = false;
// Sunucuya çıkmış kayıtlar. Bunlara gelen tekrarlar bir sonraki kayda taşınır.
const sentEntries = new WeakSet();

function authToken() {
  try {
    return localStorage.getItem("authToken");
  } catch (e) {
    return null;
  }
}

// Kısa, kişisel veri içermeyen özet. Bildirim gövdesi / lead adı loga girmez;
// içerik anahtarı (h|başlık|gövde|...) ise hash'e çevrilir.
function hash(value) {
  let h = 0;
  for (let i = 0; i < value.length; i++) {
    h = ((h << 5) - h + value.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

export function safeKey(key) {
  if (key == null) return key;
  const value = String(key);
  return value.indexOf("h|") === 0 ? "h#" + hash(value) : value.slice(0, 60);
}

const BRIEF_FIELDS = [
  "id",
  "key",
  "channel",
  "event",
  "state",
  "uid",
  "type",
  "reason",
  "remaining",
  "open",
  "duration"
];

function brief(payload) {
  if (payload == null) return "";
  if (typeof payload !== "object") {
    const value = String(payload);
    // Ham payload / içerik anahtarı bildirim metnini taşır; loga girmesin.
    if (value.indexOf("h|") === 0) return safeKey(value);
    if (/^\s*[[{]/.test(value)) return "[json " + value.length + "]";
    return value.slice(0, 80);
  }
  const out = {};
  BRIEF_FIELDS.forEach(field => {
    if (payload[field] === undefined) return;
    out[field] = field === "key" ? safeKey(payload[field]) : payload[field];
  });
  return JSON.stringify(out).slice(0, 150);
}

function errorData(err) {
  if (!err) return { message: "unknown" };
  if (typeof err !== "object") return { message: String(err).slice(0, 500) };
  return {
    name: err.name,
    message: String(err.message || err).slice(0, 500),
    stack: err.stack ? String(err.stack).slice(0, MAX_STACK) : undefined
  };
}

function scheduleFlush(baseDelay) {
  const delay = baseDelay + Math.floor(Math.random() * FLUSH_JITTER_MS);
  const dueAt = Date.now() + delay;
  if (flushTimer) {
    if (dueAt >= flushDueAt) return;
    clearTimeout(flushTimer);
  }
  flushDueAt = dueAt;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush(false);
  }, delay);
}

function flush(onUnload) {
  if (!queue.length || sending) return;
  if (Date.now() < pausedUntil || sentTotal >= MAX_PER_PAGE) {
    queue = [];
    return;
  }
  const token = authToken();
  if (!token) return;

  const limit = Math.min(BATCH_SIZE, MAX_PER_PAGE - sentTotal);
  const batch = [];
  let bytes = 0;
  while (queue.length && batch.length < limit) {
    const size = JSON.stringify(queue[0]).length;
    if (batch.length && bytes + size > MAX_BATCH_BYTES) break;
    bytes += size;
    const entry = queue.shift();
    sentEntries.add(entry);
    batch.push(entry);
  }
  sentTotal += batch.length;
  const body = { session, entries: batch };
  const url = config.endpoint + ENDPOINT;

  if (onUnload && typeof fetch === "function") {
    // Sayfa kapanırken axios isteği yarıda kalır; keepalive fetch tamamlanır.
    // (sendBeacon Authorization başlığı gönderemediği için kullanılmadı.)
    try {
      fetch(url, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify(body)
      }).catch(() => {});
    } catch (e) {
      /* yoksay */
    }
    return;
  }

  sending = true;
  axios
    .post(url, body, {
      headers: { Authorization: token },
      // Asılı kalan bir istek sonraki gönderimleri kilitlemesin.
      timeout: REQUEST_TIMEOUT_MS
    })
    .catch(err => {
      if (err && err.response) {
        if (err.response.status === 429) {
          pausedUntil = Date.now() + RATE_LIMIT_PAUSE_MS;
        }
        // Diğer sunucu hataları: kayıt düşer, tekrar denenmez (döngü olmasın).
        return;
      }
      // Ağ yok: bir sonraki turda tekrar denensin.
      sentTotal -= batch.length;
      queue = batch.concat(queue).slice(0, MAX_QUEUE);
    })
    .then(() => {
      sending = false;
      if (queue.length) scheduleFlush(FLUSH_DELAY_MS);
    });
}

/**
 * level: "info" | "warn" | "error"
 * options.dedup: false -> her çağrı ayrı kayıt (ör. her gelen bildirim).
 */
function log(level, event, data, options) {
  const opts = options || {};
  const now = Date.now();

  if (opts.dedup !== false) {
    const signature =
      level +
      "|" +
      event +
      "|" +
      ((data && (data.message || data.reason || data.code)) || "");
    const previous = recent.get(signature);
    if (previous && now - previous.ts < DEDUP_WINDOW_MS) {
      // Kayıt henüz gönderilmediyse sayacı artar; gönderildiyse tekrarlar
      // pencere bitince açılacak bir sonraki kayda eklenir.
      if (sentEntries.has(previous.entry)) previous.carry++;
      else previous.entry.count++;
      return;
    }
    recent.forEach((value, key) => {
      if (now - value.ts >= DEDUP_WINDOW_MS) recent.delete(key);
    });
    const entry = build(level, event, data);
    if (previous) entry.count += previous.carry;
    recent.set(signature, { ts: now, entry, carry: 0 });
    enqueue(entry);
    return;
  }
  enqueue(build(level, event, data));
}

function build(level, event, data) {
  const entry = {
    level,
    event,
    ts: new Date().toISOString(),
    url: typeof location !== "undefined" ? location.pathname : "",
    count: 1,
    data: data || null
  };
  if (level === "warn" || level === "error") entry.trail = trail.slice();
  return entry;
}

function enqueue(entry) {
  queue.push(entry);
  if (queue.length > MAX_QUEUE) queue.shift();
  scheduleFlush(
    entry.level === "error" ? ERROR_FLUSH_DELAY_MS : FLUSH_DELAY_MS
  );
}

/**
 * Durum özeti gibi kayıtlar için: içerik değişmediyse ttlMs boyunca (tüm
 * sekmeler ve sayfa yenilemeleri dahil) tekrar gönderilmez.
 */
function infoIfChanged(event, data, ttlMs) {
  const storageKey = "clientLog:" + event;
  const signature = hash(JSON.stringify(data || null));
  try {
    const previous = (localStorage.getItem(storageKey) || "").split("|");
    if (
      previous[0] === signature &&
      Date.now() - (parseInt(previous[1], 10) || 0) < ttlMs
    ) {
      return;
    }
    localStorage.setItem(storageKey, signature + "|" + Date.now());
  } catch (e) {
    /* depolama yok: yine de gönder */
  }
  log("info", event, data);
}

// Hata kaydına eklenecek son adımlar (sunucuya tek başına gitmez).
function trace(stage, payload) {
  const time = new Date().toTimeString().slice(0, 8);
  trail.push(
    time + " " + stage + (payload === undefined ? "" : " " + brief(payload))
  );
  if (trail.length > TRAIL_SIZE) trail.shift();
}

function ignorable(message, source) {
  const text = String(message || "");
  return (
    text.indexOf("ResizeObserver loop") !== -1 ||
    /^(chrome|moz|safari)-extension:/.test(String(source || "")) ||
    text === "Script error."
  );
}

// Global hata yakalayıcılar. main.js'te bir kez çağrılır.
function install(Vue) {
  if (installed) return;
  installed = true;

  window.addEventListener(
    "error",
    event => {
      const target = event.target;
      if (target && target !== window) {
        // Sadece JS/CSS dosyası yüklenemedi (deploy sonrası eski chunk vb.).
        if (target.tagName === "SCRIPT" || target.tagName === "LINK") {
          log("warn", "resource.error", {
            tag: target.tagName,
            src: String(target.src || target.href || "").slice(0, 200)
          });
        }
        return;
      }
      if (ignorable(event.message, event.filename)) return;
      log("error", "js.error", {
        message: String(event.message || "").slice(0, 500),
        source: String(event.filename || "").slice(0, 200),
        line: event.lineno,
        col: event.colno,
        stack:
          event.error && event.error.stack
            ? String(event.error.stack).slice(0, MAX_STACK)
            : undefined
      });
    },
    true
  );

  window.addEventListener("unhandledrejection", event => {
    const reason = event.reason;
    // Beklenen redler: axios hataları (4xx/5xx, ekranda zaten gösteriliyor),
    // iptal edilen istekler ve vue-router'ın aynı sayfaya gitme reddi
    // (NavigationDuplicated vb.). Bunlar log şişirmesin.
    if (
      reason &&
      (reason.isAxiosError || reason.__CANCEL__ || reason._isRouter)
    ) {
      return;
    }
    const data = errorData(reason);
    if (ignorable(data.message)) return;
    log("error", "js.unhandledrejection", data);
  });

  if (Vue) {
    const previous = Vue.config.errorHandler;
    Vue.config.errorHandler = (err, vm, info) => {
      // vue-router'ın "aynı sayfaya gitme" reddi (NavigationDuplicated vb.)
      // bir handler'dan dönen promise ile buraya da gelir; hata değil.
      if (err && err._isRouter) {
        if (previous) previous(err, vm, info);
        return;
      }
      const data = errorData(err);
      data.info = info;
      data.component =
        vm && vm.$options
          ? vm.$options.name || vm.$options._componentTag
          : null;
      log("error", "vue.error", data);
      if (previous) previous(err, vm, info);
      else console.error(err); // eslint-disable-line no-console
    };
  }

  window.addEventListener("pagehide", () => flush(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
}

export default {
  install,
  log,
  info: (event, data, options) => log("info", event, data, options),
  warn: (event, data, options) => log("warn", event, data, options),
  error: (event, data, options) => log("error", event, data, options),
  infoIfChanged,
  trace,
  errorData
};
